import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Wallet } from 'lucide-react';
import OverviewPanel from '../components/OverviewPanel';
import PayrollPanel from '../components/PayrollPanel';
import ExpensesPanel from '../components/ExpensesPanel';
import DebtsPanel from '../components/DebtsPanel';
import CollectionPanel from '../components/CollectionPanel';
import MultasPanel from '../components/MultasPanel';
import IngresosApartado from '../components/IngresosApartado';
import PresupuestoTab from '../components/PresupuestoTab';
import { useAuth } from '../../../context/AuthContext';

// Conceptos que escribe la nómina sola (pago de nómina y extras): ya salen en la nómina de arriba.
const EXCLUIR_NOMINA = ['nomina', 'bono_puntualidad', 'bono_desempeno'];

export default function FinancesPage() {
  const { hasModule } = useAuth();
  // La CAJA ya no es pestaña: vive como pantalla principal /caja (sidebar), como en la agenda
  // dental. Y desde el 18-sep-2026 la lista de INGRESOS tampoco vive aquí: es `IngresosPanel`,
  // el mismo componente que usa la pestaña «Ingresos» de /caja, para que no haya dos listas de
  // cobros que se vayan separando con el tiempo.
  // 23-sep-2026 (Carlos): la pestaña «Ingresos» se QUITÓ de aquí — era el mismo IngresosPanel que
  // Caja → Ingresos, repetido. Los enlaces viejos (/finanzas?nuevo=pago) se mandan a Caja.
  // 7-oct-2026 (Mabel): las pestañas siguen los 4 apartados del catálogo — Ingresos · Gastos · Nómina ·
  // Inversiones — y Cobranza / Adeudos / Pagos tardíos se quedan (lo pidió explícitamente). «Ingresos» NO es
  // la lista de cobros de Caja repetida (eso se quitó el 23-sep): es el resumen por categoría y concepto.
  // 15-oct-2026 (B2, Mabel): «Presupuesto» = captura del presupuesto de gastos por concepto × mes.
  const [tab, setTab] = useState<'general' | 'ingresos' | 'cobranza' | 'gastos' | 'nomina' | 'inversiones' | 'presupuesto' | 'adeudos' | 'multas'>('general');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // /finanzas?tab=adeudos&ver=desfasados (tarjeta «Desfasados» del Inicio, Mabel 3-nov-2026).
  const [verDesf, setVerDesf] = useState(false);

  // Atajo del FAB móvil: /finanzas?nuevo=pago → pestaña «Ingresos» + abre «Registrar pago».
  useEffect(() => {
    const pt = params.get('tab');
    if (pt === 'multas' || pt === 'presupuesto' || pt === 'adeudos') {
      setTab(pt);
      if (pt === 'adeudos' && params.get('ver') === 'desfasados') setVerDesf(true);
      params.delete('tab');
      params.delete('ver');
      setParams(params, { replace: true });
    }
    if (params.get('nuevo') === 'pago') navigate('/caja?t=ingresos&nuevo=pago', { replace: true });
  }, [params, setParams, navigate]);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-content"><Wallet className="text-brand-blue" size={24} /> Finanzas</h2>
      </div>

      {/* Pestañas Ingresos / Gastos / Adeudos */}
      {/* 🚨 19-sep-2026: con `inline-flex` a secas las seis pestañas medían 554px en un
          teléfono de 390 y «Nómina» y «Adeudos» quedaban FUERA de la pantalla, sin scroll que
          las alcanzara: desde el móvil esas dos pestañas no existían. `flex-wrap` las baja a un
          segundo renglón dentro del mismo recuadro; en escritorio siguen en una sola línea. */}
      <div className="mb-5 inline-flex max-w-full flex-wrap rounded-lg border border-line bg-surface p-1">
        {([['general', 'Vista general'], ['ingresos', 'Ingresos'], ['gastos', 'Gastos'], ['nomina', 'Nómina'], ['inversiones', 'Inversiones'], ['presupuesto', 'Presupuesto'], ['cobranza', 'Cobranza'], ['adeudos', 'Adeudos'], ['multas', 'Pagos tardíos']] as const)
          .filter(([k]) => (k !== 'nomina' || hasModule('letsmove_payroll')))
          .map(([k, label]) => (
          <button key={k} onClick={() => { setTab(k); setVerDesf(false); }}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition ${
              tab === k ? 'bg-accent text-on-accent shadow-sm' : 'text-content-2 hover:text-content'
            }`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'general' ? (
        <OverviewPanel />
      ) : tab === 'ingresos' ? (
        <IngresosApartado />
      ) : tab === 'cobranza' ? (
        <CollectionPanel />
      ) : tab === 'gastos' ? (
        <ExpensesPanel conProgramados />
      ) : tab === 'nomina' ? (
        <>
          <PayrollPanel />
          {/* Mabel 7-oct-2026: «aquí en nómina sí puedes agregar abajo otros apartados para capturar
              capacitación, eventos, etc.». Sin la nómina y los bonos, que ya salen arriba. */}
          <div className="mt-8 border-t border-line pt-6">
            <h3 className="mb-3 font-display text-lg font-bold text-content">Otros gastos de personal</h3>
            <ExpensesPanel secciones={['nomina']} excluir={EXCLUIR_NOMINA} titulo="Otros gastos de personal"
              textoBoton="Registrar gasto de personal" conCatalogo={false} vacio="Sin capacitaciones ni eventos" />
          </div>
        </>
      ) : tab === 'inversiones' ? (
        <ExpensesPanel secciones={['inversion']} titulo="Total de inversiones" textoBoton="Registrar inversión"
          conCatalogo={false} vacio="Sin inversiones" />
      ) : tab === 'presupuesto' ? (
        <PresupuestoTab />
      ) : tab === 'adeudos' ? (
        <DebtsPanel enfocarDesfasados={verDesf} />
      ) : (
        <MultasPanel />
      )}
    </div>
  );
}
